<template>
  <!-- ==================== 앱 루트 레이아웃 ==================== -->
  <div>
    <!-- ==================== 웹 플랫폼 레이아웃 ==================== -->
    <div
      v-if="platform === 'web'"
      class="app-layout"
      :class="{
        'app-layout--desktop': isPcRedesign && menuYn,
        'app-layout--fallback': !isPcRedesign,
        'app-layout--collapsed': sidebarCollapsed && isPcRedesign && menuYn
      }"
    >
      <header-menu
        v-if="menuYn && isPcRedesign"
        class="app-sidebar"
        :collapsed="sidebarCollapsed"
        @toggle-collapse="sidebarCollapsed = !sidebarCollapsed"
      />
      <app-header v-if="menuYn && isPcRedesign" class="app-header" />
      <mobile-header
        v-if="menuYn && !isPcRedesign"
        @toggle-drawer="drawerOpen = !drawerOpen"
      />
      <mobile-drawer
        v-if="menuYn && !isPcRedesign"
        v-model="drawerOpen"
      />
      <div v-if="menuYn" class="app-content" :class="{ 'app-content--fallback': !isPcRedesign }">
        <!-- ==================== TabWorkspace (title + tab pill + actions + router-view) ==================== -->
        <tab-workspace>
          <template #title>{{ pageTitle }}</template>
          <template #actions>
            <partner-select
              v-if="showPartnerSelect"
              @select-partner="onPartnerChange"
            />
            <!-- 페이지 전용 PartnerSelect 등 actions 주입 대상 (Vue Teleport) -->
            <div id="app-actions-extra" class="app-actions-extra"></div>
          </template>
        </tab-workspace>
      </div>
      <!-- ==================== 메뉴 없는 라우터 뷰 (로그인 등) ==================== -->
      <router-view v-else />
    </div>
    <!-- ==================== 모바일 플랫폼 레이아웃 ==================== -->
    <div v-else-if="platform === 'mobile'" class="mobile-container">
      <router-view/>
    </div>
    <!-- ==================== 기타 플랫폼 폴백 ==================== -->
    <div v-else>
      <router-view/>
    </div>
    <!-- ==================== 전역 토스트 알림 ==================== -->
    <app-toast />
    <!-- ==================== 전역 운영 어시스턴트 (플로팅 챗봇) ==================== -->
    <assistant-widget v-if="platform === 'web' && menuYn" />
  </div>
</template>

<script>
/**
 * @module App
 * @description 앱 루트 컴포넌트 — 플랫폼(web/mobile)에 따라 레이아웃을 분기하고,
 * 인증 토큰 기반으로 언어를 설정합니다.
 * PC 리디자인 여부(`isPcRedesign`)에 따라 사이드바·헤더 그리드 레이아웃이 적용됩니다.
 * TabWorkspace 의 title / actions 슬롯에 화면명과 PartnerSelect 를 주입합니다.
 * PartnerSelect 는 route meta.partnerSelect === true 인 라우트에서만 표시됩니다.
 */
import AppHeader from '@/components/layouts/AppHeader.vue'
import HeaderMenu from '@/components/layouts/HeaderMenu.vue'
import AppToast from '@/components/common/AppToast.vue'
import TabWorkspace from '@/components/layouts/TabWorkspace.vue'
import MobileHeader from '@/components/layouts/MobileHeader.vue'
import MobileDrawer from '@/components/layouts/MobileDrawer.vue'
import PartnerSelect from '@/components/selectBox/PartnerSelect.vue'
import AssistantWidget from '@/components/assistant/AssistantWidget.vue'
import { useResponsive } from '@/composables/useResponsive'
import { mapState as mapPiniaState } from 'pinia'
import { useTabStore } from '@/stores/tabs'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'

export default {
  components: {
    AppHeader,
    HeaderMenu,
    AppToast,
    TabWorkspace,
    MobileHeader,
    MobileDrawer,
    PartnerSelect,
    AssistantWidget
  },
  setup() {
    const { isPcRedesign } = useResponsive()
    const tabStore = useTabStore()
    return { isPcRedesign, tabStore }
  },
  data() {
    return {
      language: 'ko',
      platform: 'web',
      menuYn: false,
      drawerOpen: false,
      sidebarCollapsed: false
    }
  },
  mounted () {
    /**
     * 마운트 시 세션 스토리지의 authToken을 확인하여
     * JWT에서 언어 설정을 읽어 vue-i18n 로케일을 초기화합니다.
     * menuStore 로드는 HeaderMenu / MobileDrawer 에서 담당합니다.
     */
    const authToken = sessionStorage.getItem('authToken')

    if (authToken) {
      this.language = this.$tokenDecode().language
      this.changeLocale(this.language)
    }
  },
  watch: {
    '$route'(to) {
      this.platform = to.meta.platform
      this.menuYn = to.meta.menuYn
    }
  },
  computed: {
    ...mapPiniaState(useTabStore, ['activeTabTitle']),
    user() {
      return this.store.state.auth.user
    },
    /**
     * 페이지 타이틀 바에 표시할 화면명(활성 탭 제목 = 메뉴 마스터 이름).
     * 활성 탭이 없으면 라우트 meta.title → name 을 사용합니다 (SURINP-688).
     */
    pageTitle() {
      return this.activeTabTitle || this.$route?.meta?.title || this.$route?.name || ''
    },
    /**
     * PartnerSelect 노출 조건:
     * - ADMIN 사용자
     * - PC 리디자인 활성
     * - 현재 route.meta.partnerSelect === true
     */
    showPartnerSelect() {
      const userType = this.$store.state.auth?.user?.id?.userType
      return (
        userType === 'ADMIN' &&
        this.isPcRedesign &&
        this.$route?.meta?.partnerSelect === true
      )
    }
  },
  methods: {
    /**
     * vue-i18n 로케일을 변경합니다.
     *
     * @param {string} language - 적용할 언어 코드 (예: 'ko', 'en', 'jp')
     */
    changeLocale(language) {
      this.$i18n.locale = language
    },
    /**
     * PartnerSelect 변경 시 호출됩니다. Vuex 스토어는 PartnerSelect 내부에서
     * setPartnerId mutation 으로 이미 갱신되므로, 각 view 는 store watch 로 반응합니다.
     * (App.vue 레벨에서는 별도 처리 불필요 — no-op)
     * @param {string} _partnerId
     */
    onPartnerChange(_partnerId) {
      // noop: 각 view 가 store 를 watch 하여 재조회합니다.
    }
  }
}
</script>

<style>
@import '@/assets/css/index.css';

.app-layout {
  min-height: 100vh;
  background: var(--pc-surface-subtle, var(--color-gray-50, #F2F3F5));
}

.app-content {
  min-height: 100vh;
  background: transparent;
}

.app-actions-extra {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 8px);
}

/* 페이지 전용 actions (예: 상담이력 고객사 Select) — teleport target 내부 공통 스타일 */
.app-actions-extra .consultation-partner-select {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 8px);
}
.app-actions-extra .consultation-partner-select__label {
  font-size: var(--font-body-sm, 0.8125rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--color-gray-600, #424750);
  white-space: nowrap;
}

.app-content--fallback {
  min-height: calc(100vh - 56px);
}

@media (min-width: 1280px) {
  .app-layout--desktop {
    display: grid;
    grid-template-columns: var(--sidebar-width, 260px) minmax(0, 1fr);
    grid-template-rows: var(--header-height, 64px) minmax(0, 1fr);
    grid-template-areas:
      "sidebar header"
      "sidebar content";
    height: 100vh;
    overflow: hidden;
    background: var(--color-gray-50, #F2F3F5);
  }

  .app-layout--collapsed {
    grid-template-columns: var(--sidebar-collapsed-width, 64px) minmax(0, 1fr);
  }

  .app-sidebar {
    grid-area: sidebar;
    min-width: 0;
    overflow: hidden;
  }

  .app-header {
    grid-area: header;
    position: sticky;
    top: 0;
    z-index: var(--z-header, 1000);
    isolation: isolate;
    min-width: 0;
  }

  .app-content {
    grid-area: content;
    min-height: auto;
    min-width: 0;
    overflow-y: auto;
    padding-bottom: var(--space-6, 24px);
  }
}

/* AG Grid Redesign */
.ag-theme-alpine {
  --ag-grid-size: 4px;
  --ag-list-item-height: 20px;
  /* AG Grid 32 는 행·헤더 배치를 이 변수로만 정한다 — 아래 .ag-row / .ag-header-row 강제 높이와 맞춘다 (SURINP-616) */
  --ag-row-height: var(--table-row-height, 40px);
  --ag-header-height: var(--table-header-height, 34px);
}
.ag-header-viewport {
  background: var(--color-gray-50, #F2F3F5);
  transition: background var(--transition-normal, 0.2s ease);
}
.ag-header-cell {
  text-align: center;
  font-size: var(--font-caption-sm, 0.6875rem);
  font-weight: var(--font-weight-semibold, 600);
}
.ag-paging-panel {
  text-align: center;
}
.ag-row {
  height: var(--table-row-height, 40px) !important;
}
.ag-row:hover {
  background: var(--color-primary-50, #E8EDF4);
}
.ag-cell {
  padding-left: 14px;
  font-size: var(--font-body-sm, 0.8125rem);
  line-height: var(--table-row-height, 40px);
}
.ag-header-row {
  height: var(--table-header-height, 34px) !important;
  background: var(--color-gray-50, #F2F3F5);
  font-size: var(--font-caption-sm, 0.6875rem);
  font-weight: var(--font-weight-semibold, 600);
}

@media (max-width: 1279px) {
  .app-layout {
    display: block;
  }
  .app-sidebar {
    display: none;
  }
}
</style>
